'use client';

import * as React from 'react';

import { FocusScope } from '@radix-ui/react-focus-scope';
import { useKey, useLockBodyScroll } from 'react-use';

import { cn } from '@/lib/utils';
import { isInIframe } from '@/lib/utils/dom';

const SIDEBAR_WIDTH_MOBILE = '18rem';

interface SidebarMobileDrawerProps {
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  side: 'start' | 'end';
  children: React.ReactNode;
}

export function SidebarMobileDrawer({ isOpen, setOpen, side, children }: SidebarMobileDrawerProps) {
  const drawerRef = React.useRef<HTMLDivElement>(null);
  const previousFocusRef = React.useRef<HTMLElement | null>(null);
  const handleClose = React.useCallback(() => setOpen(false), [setOpen]);
  const inIframe = React.useMemo(() => isInIframe(), []);

  useKey('Escape', handleClose);
  useLockBodyScroll(isOpen && !inIframe);

  React.useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      requestAnimationFrame(() => drawerRef.current?.focus());
    } else {
      previousFocusRef.current?.focus();
      previousFocusRef.current = null;
    }
  }, [isOpen]);

  return (
    <>
      <div
        data-slot="sidebar-overlay"
        data-state={isOpen ? 'open' : 'closed'}
        className={cn(
          'absolute inset-0 z-50 bg-black/50 transition-opacity duration-300 ease-in-out',
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        onClick={handleClose}
        aria-hidden="true"
      />
      <FocusScope
        ref={drawerRef}
        trapped={isOpen}
        loop
        onMountAutoFocus={e => e.preventDefault()}
        onUnmountAutoFocus={e => e.preventDefault()}
        role="dialog"
        aria-modal={isOpen || undefined}
        aria-label="Sidebar"
        inert={!isOpen || undefined}
        tabIndex={-1}
        data-sidebar="sidebar"
        data-slot="sidebar"
        data-mobile="true"
        data-state={isOpen ? 'open' : 'closed'}
        className={cn(
          'absolute z-50 w-(--sidebar-width) bg-sidebar p-0 text-sidebar-foreground shadow-none outline-hidden',
          'inset-y-0 transition-transform duration-300 ease-in-out',
          side === 'start' && 'start-0 border-e border-border',
          side === 'start' && (isOpen ? 'translate-x-0' : '-translate-x-full rtl:translate-x-full'),
          side === 'end' && 'end-0 border-s border-border',
          side === 'end' && (isOpen ? 'translate-x-0' : 'translate-x-full rtl:-translate-x-full'),
        )}
        style={{ '--sidebar-width': SIDEBAR_WIDTH_MOBILE } as React.CSSProperties}
      >
        <div className="flex h-full w-full flex-col">{children}</div>
      </FocusScope>
    </>
  );
}
